// src/components/progress/InsuranceUploadProgressDisplay.js

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * プログレス表示コンポーネント
 * ファイルアップロードや申請処理の進捗を視覚的に表示
 */
const InsuranceUploadProgressDisplay = ({ 
  isVisible = false,
  stage = 'preparing',
  message = '',
  detail = '',
  progress = 0,
  uploadProgress = 0,
  timeRemaining = null,
  onCancel = null,
  onComplete = null, // 完了時のコールバック関数を追加
  size = 'large', // 'small', 'medium', 'large'
  theme = 'default' // 'default', 'dark', 'minimal'
}) => {
  // CSS アニメーション用のキーフレーム（インラインスタイルで定義）
  const animationStyles = `
    @keyframes progressPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.02); }
    }
    
    @keyframes progressShimmer {
      0% { left: -100%; }
      100% { left: 100%; }
    }
    
    @keyframes fadeInScale {
      0% { opacity: 0; transform: scale(0.8); }
      100% { opacity: 1; transform: scale(1); }
    }
  `;

  // インジェクトするスタイル - useEffectを最上位に配置
  useEffect(() => {
    if (isVisible && !document.getElementById('progress-animations')) {
      const style = document.createElement('style');
      style.id = 'progress-animations';
      style.textContent = animationStyles;
      document.head.appendChild(style);
    }
    
    // クリーンアップ
    return () => {
      if (!isVisible) {
        const style = document.getElementById('progress-animations');
        if (style) {
          style.remove();
        }
      }
    };
  }, [isVisible, animationStyles]);

  // 非表示の場合は何も表示しない（useEffectの後に配置）
  if (!isVisible) return null;

  // ステージカラーの取得
  const getStageColor = (stage) => {
    switch (stage) {
      case 'preparing': return '#17a2b8';
      case 'validating': return '#ffc107';
      case 'uploading': return '#007bff';
      case 'processing': return '#6f42c1';
      case 'complete': return '#28a745';
      case 'error': return '#dc3545';
      case 'cancelled': return '#6c757d';
      default: return '#6c757d';
    }
  };

  // サイズ設定
  const getSizeConfig = (size) => {
    switch (size) {
      case 'small':
        return {
          width: '300px',
          padding: '20px',
          iconSize: '32px',
          titleSize: '16px',
          progressHeight: '12px'
        };
      case 'medium':
        return {
          width: '400px',
          padding: '30px',
          iconSize: '40px',
          titleSize: '18px',
          progressHeight: '16px'
        };
      case 'large':
      default:
        return {
          width: '500px',
          padding: '40px',
          iconSize: '48px',
          titleSize: '20px',
          progressHeight: '20px'
        };
    }
  };

  // テーマ設定
  const getThemeConfig = (theme) => {
    switch (theme) {
      case 'dark':
        return {
          backgroundColor: '#2d3748',
          textColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.9)',
          borderColor: '#4a5568'
        };
      case 'minimal':
        return {
          backgroundColor: '#ffffff',
          textColor: '#333333',
          overlayColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e2e8f0'
        };
      case 'default':
      default:
        return {
          backgroundColor: '#ffffff',
          textColor: '#333333',
          overlayColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: '#e0e0e0'
        };
    }
  };

  const sizeConfig = getSizeConfig(size);
  const themeConfig = getThemeConfig(theme);
  const stageColor = getStageColor(stage);

  // アニメーション中かどうか
  const isAnimating = ['preparing', 'validating', 'uploading', 'processing'].includes(stage);

  // 完了時のOKボタン押下ハンドラ
  const handleCompleteOk = () => {
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div 
      className="advanced-progress-overlay" 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: themeConfig.overlayColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(4px)',
        transition: 'opacity 0.3s ease'
      }}
      onClick={(e) => {
        // 完了時とエラー時はオーバーレイクリックを無効化
        if (stage === 'complete' || stage === 'error') {
          return;
        }
        // オーバーレイ部分をクリックした場合のみキャンセル
        if (e.target === e.currentTarget && onCancel) {
          onCancel();
        }
      }}
    >
      <div 
        className="advanced-progress-content" 
        style={{
          backgroundColor: themeConfig.backgroundColor,
          padding: sizeConfig.padding,
          borderRadius: '16px',
          minWidth: sizeConfig.width,
          maxWidth: '90vw',
          textAlign: 'center',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
          border: `1px solid ${themeConfig.borderColor}`,
          position: 'relative',
          animation: isAnimating ? 'progressPulse 2s ease-in-out infinite' : 
                     stage === 'complete' ? 'fadeInScale 0.5s ease-out' : 'none'
        }}
      >
        {/* キャンセルボタン */}
        {onCancel && stage !== 'complete' && stage !== 'error' && (
          <button
            onClick={onCancel}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'none',
              border: 'none',
              fontSize: '20px',
              color: '#999',
              cursor: 'pointer',
              padding: '5px',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
            title="キャンセル"
          >
            ×
          </button>
        )}
        
        {/* 完了時のアイコン */}
        {stage === 'complete' && (
          <div
            style={{
              fontSize: sizeConfig.iconSize,
              color: '#28a745',
              marginBottom: '15px',
              animation: 'fadeInScale 0.8s ease-out 0.2s both'
            }}
          >
            ✓
          </div>
        )}

        {/* エラー時のアイコン */}
        {stage === 'error' && (
          <div
            style={{
              fontSize: sizeConfig.iconSize,
              color: '#dc3545',
              marginBottom: '15px',
              animation: 'fadeInScale 0.8s ease-out 0.2s both'
            }}
          >
            ⚠
          </div>
        )}
        
        {/* メインメッセージ */}
        <div 
          style={{
            fontSize: sizeConfig.titleSize,
            fontWeight: '600',
            color: themeConfig.textColor,
            marginBottom: '15px',
            marginTop: '10px',
            lineHeight: '1.2'
          }}
        >
          {message}
        </div>
        
        {/* 詳細プログレスバー（完了時とエラー時以外） */}
        {stage !== 'complete' && stage !== 'error' && (
          <>
            <div 
              style={{
                width: '100%',
                height: sizeConfig.progressHeight,
                backgroundColor: theme === 'dark' ? '#4a5568' : '#f0f0f0',
                borderRadius: `${parseInt(sizeConfig.progressHeight) / 2}px`,
                overflow: 'hidden',
                marginBottom: '10px',
                position: 'relative'
              }}
            >
              {/* メインプログレス */}
              <div
                style={{
                  width: `${Math.max(0, Math.min(100, progress))}%`,
                  height: '100%',
                  backgroundColor: stageColor,
                  borderRadius: `${parseInt(sizeConfig.progressHeight) / 2}px`,
                  transition: 'width 0.5s ease',
                  background: `linear-gradient(90deg, ${stageColor}, ${stageColor}aa)`,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* アニメーション効果 */}
                {isAnimating && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                      animation: 'progressShimmer 2s infinite'
                    }}
                  />
                )}
              </div>
              
              {/* アップロード進捗の二重表示 */}
              {stage === 'uploading' && uploadProgress > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${Math.max(0, Math.min(100, uploadProgress * 0.5))}%`,
                    height: '100%',
                    backgroundColor: '#28a745',
                    borderRadius: `${parseInt(sizeConfig.progressHeight) / 2}px`,
                    opacity: 0.7
                  }}
                />
              )}
            </div>
            
            {/* 進捗数値 */}
            <div 
              style={{
                fontSize: size === 'small' ? '14px' : '18px',
                color: stageColor,
                fontWeight: 'bold',
                marginBottom: '15px'
              }}
            >
              {Math.round(Math.max(0, Math.min(100, progress)))}%
              {stage === 'uploading' && uploadProgress > 0 && (
                <span style={{ 
                  fontSize: size === 'small' ? '11px' : '14px', 
                  marginLeft: '10px', 
                  color: theme === 'dark' ? '#cbd5e0' : '#666' 
                }}>
                  (アップロード: {Math.round(uploadProgress)}%)
                </span>
              )}
            </div>
          </>
        )}
        
        {/* 詳細情報 */}
        {detail && (
          <div 
            style={{
              fontSize: size === 'small' ? '12px' : '14px',
              color: theme === 'dark' ? '#cbd5e0' : '#666',
              marginBottom: '10px',
              lineHeight: '1.4',
              wordBreak: 'break-word'
            }}
          >
            {detail}
          </div>
        )}
        
        {/* 残り時間 */}
        {timeRemaining && stage !== 'complete' && stage !== 'error' && (
          <div 
            style={{
              fontSize: size === 'small' ? '10px' : '12px',
              color: theme === 'dark' ? '#a0aec0' : '#999',
              fontStyle: 'italic',
              marginBottom: '10px'
            }}
          >
            {timeRemaining}
          </div>
        )}
        
        {/* ステージ別メッセージ */}
        {stage !== 'complete' && (
          <div 
            style={{
              fontSize: size === 'small' ? '10px' : '12px',
              color: theme === 'dark' ? '#a0aec0' : '#999',
              marginTop: '20px'
            }}
          >
            {stage === 'error' ?
              'エラーが発生しました。詳細を確認してください。' :
              stage === 'cancelled' ?
              '処理がキャンセルされました。' :
              'しばらくお待ちください...'
            }
          </div>
        )}
        
        {/* 完了時のOKボタン */}
        {stage === 'complete' && onComplete && (
          <div
            style={{
              marginTop: '30px',
              animation: 'fadeInScale 0.8s ease-out 0.4s both'
            }}
          >
            <button
              onClick={handleCompleteOk}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: size === 'small' ? '10px 20px' : '12px 30px',
                fontSize: size === 'small' ? '14px' : '16px',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '120px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#218838';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#28a745';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(40, 167, 69, 0.3)';
              }}
              onFocus={(e) => {
                e.target.style.outline = '2px solid #28a745';
                e.target.style.outlineOffset = '2px';
              }}
              onBlur={(e) => {
                e.target.style.outline = 'none';
              }}
            >
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// PropTypesの定義
InsuranceUploadProgressDisplay.propTypes = {
  isVisible: PropTypes.bool,
  stage: PropTypes.oneOf([
    'preparing', 'validating', 'uploading', 'processing', 'complete', 'error', 'cancelled'
  ]),
  message: PropTypes.string,
  detail: PropTypes.string,
  progress: PropTypes.number,
  uploadProgress: PropTypes.number,
  timeRemaining: PropTypes.string,
  onCancel: PropTypes.func,
  onComplete: PropTypes.func, // 完了時のコールバック関数を追加
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  theme: PropTypes.oneOf(['default', 'dark', 'minimal'])
};

export default InsuranceUploadProgressDisplay;